import React from "react";

import { dictionaries } from "@/dictionaries/content";
import type { ISchoolLastActionResponse } from "@/providers/store/api/schools/schools.interface";
import { formatNumber } from "@/shared/utils/format-number.util";
import { getScheduledActionDisplayItem } from "@/shared/utils/get-scheduled-action-display-item.util";

import { SectionBasicInfoList } from "./section-basic-info-list";
import { SectionTitle } from "./section-title";
import { SectionTitleNavLink } from "./section-title-nav-link";

interface BasicInformationProps {
  name: string;
  schoolId: string;
  schoolDriveId?: string;
  schoolTypeLabels?: string[];
  gradeServed: string;
  principalName: string;
  phoneNumber: string;
  schoolURL: string;
  schoolURLHref: string;
  address: string;
  capacity: string;
  educationalProgramType?: string | null;
  closedAt?: string;
  featureAction: ISchoolLastActionResponse | null;
}

export const BasicInformation: React.FC<BasicInformationProps> = ({
  name,
  schoolId,
  schoolDriveId,
  schoolTypeLabels,
  gradeServed,
  principalName,
  phoneNumber,
  schoolURL,
  schoolURLHref,
  address,
  capacity,
  educationalProgramType,
  closedAt,
  featureAction,
}) => {
  const scheduledActionItem = featureAction ? getScheduledActionDisplayItem(featureAction) : null;
  const isClosed = Boolean(closedAt);
  const hasSchoolURL = Boolean(schoolURL?.trim()) && Boolean(schoolURLHref?.trim());

  return (
    <div>
      <SectionTitle
        title="Basic Info"
        trailing={
          schoolDriveId ? (
            <SectionTitleNavLink
              href={`/panel/my-district/${schoolDriveId}/view`}
              label={dictionaries.mapPage.schoolPanel.goToFullInfo}
            />
          ) : undefined
        }
      />

      <SectionBasicInfoList
        items={[
          { type: "text", head: "Name", value: name ?? "--" },
          { type: "badge", head: "Status", variant: isClosed ? "default" : "green", value: isClosed ? "Closed" : "Open" },
          ...(scheduledActionItem ? [scheduledActionItem] : []),
          ...(schoolTypeLabels?.length
            ? [{ type: "text" as const, head: "School types", value: schoolTypeLabels.join(", ") }]
            : []),
          { type: "text", head: "School ID", value: schoolId ?? "--" },
          { type: "text", head: "Grade Served", value: gradeServed ?? "--" },
          { type: "text", head: "Educational Program Type", value: educationalProgramType ?? "--" },
          { type: "text", head: "Principal Name", value: principalName ?? "--" },
          { type: "text", head: "Phone Number ", value: phoneNumber ?? "--" },
          hasSchoolURL
            ? {
              type: "link" as const,
              head: "School URL ",
              value: schoolURL,
              href: schoolURLHref,
            }
            : { type: "text" as const, head: "School URL ", value: "-" },
          { type: "text", head: "Address", value: address ?? "--" },
          { type: "text", head: "Current Capacity", value: formatNumber(capacity ?? "--") },
        ]}
      />
    </div>
  );
};
